/**
 * THE BOOTH, ON AIR.
 *
 * The booth calls a whole race in one deterministic pass, but a race is watched
 * a turn at a time — so this component reveals the call as the cursor advances
 * and holds the most recent beat on air. Dumping the finished transcript at the
 * flag would be accurate and would feel nothing like a broadcast.
 *
 * WHAT IS SHOWN, AND WHY IT MATTERS THAT IT IS AUDITABLE. Every beat carries
 * the rule citation that produced the moment and the facts the sentence rests
 * on, both openable. That is not decoration: this project's whole claim is that
 * the prose is downstream of the dice, and a claim nobody can check is a claim
 * nobody should believe. Open a beat and you can see the rulebook page and the
 * numbers behind every clause.
 *
 * THE SILENCE IS PART OF IT. Most turns produce no beat at all — a fifty-turn
 * race is 10–20 of them out of ~130 events — so the booth holds its last line
 * rather than filling the gap. A panel that said something every turn would be
 * the exact failure NARRATION_MODEL §8.1 is about.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { RaceResult } from '../domain/race.ts';
import { type BoothBeat } from '../domain/booth/booth.ts';
import type { BoothContext, BoothSeasonContext } from '../domain/booth/context.ts';
import type { FeudHistory } from '../domain/feudHistory.ts';
import type { Team } from '../domain/field.ts';
import { beatEndsAt } from '../domain/booth/airTime.ts';
import { settingFor as buildSetting } from '../domain/booth/setting.ts';
import { buildBroadcast, raceStatus } from '../domain/booth/broadcast.ts';
import { castCall, narrateBeat, narrationAvailable, type NarratedBeat } from '../lib/narrate.ts';
import { DEFAULT_CAST, type Cast, type MemberId } from '../domain/booth/cast.ts';
import type { BoothLeague } from '../domain/booth/league.ts';
import { Cite } from './Cite.tsx';
import { useShowCitations } from '../lib/prefs.ts';

export interface BoothCallProps {
  result: RaceResult;
  /** The turn currently being shown. Beats after it stay unaired. */
  cursor: number;
  /** Show the whole call regardless of cursor — used after the flag. */
  complete?: boolean;
  /**
   * THE GARAGE THAT ENTERED THIS RACE — D-EZ-74.
   *
   * `Car.card.teamId` is an id; the name painted on the hauler lives on `Team`,
   * which is roster data and not race data. Without this prop the booth is
   * watching cars it cannot name the shop of, which is why "he runs for Ballew
   * & Yeargin" was structurally unsayable rather than merely unwritten.
   *
   * Optional, like every other field of `BoothContext`: an exhibition beat
   * replayed with no garage to hand gets exactly the call it always got.
   */
  teams?: readonly Team[];
  /**
   * The season's feuds as `feudHistory.ts` reads them out of the event stream.
   * This is what turns "these two are not done with each other" into "this has
   * been running since Bristol — three races now". Absent outside a season.
   */
  feudHistory?: readonly FeudHistory[];
  /**
   * Which race of the season this is. Only used to keep the booth from citing
   * the racetrack it is standing at — see `feudRead.ts`. Absent for exhibitions.
   */
  raceNumber?: number;
  /**
   * D-EZ-102 — what the league knew going into this race (points leader,
   * bubble, defending winner, milestones in play). Built from the season as it
   * stood BEFORE this race (`story/league.ts` `stateBeforeRace`). Absent for an
   * exhibition, and the booth is then exactly what it was.
   */
  season?: BoothSeasonContext;
  /**
   * D-EZ-111 — the booth's league going into this race (`boothLeagueFor`): the
   * cast, the people layer and rivalry book cut at the PREVIOUS race, last
   * week and the analyst's pick. Absent for an exhibition, which gets the
   * default booth and a pre-race open from the grid alone.
   */
  league?: BoothLeague | null;
}

/**
 * Every name a beat is entitled to mention.
 *
 * Taken from the beat's own facts, NOT from the race, because `verifyLine`
 * builds its vocabulary the same way — a model that was shown two drivers may
 * write about two drivers. Pulled off the fact text rather than the ids since
 * the facts are already written in names.
 */
function namesIn(beat: BoothBeat): string[] {
  const out = new Set<string>();
  for (const fact of beat.facts) {
    for (const match of fact.text.matchAll(/\b[A-Z][a-z']+(?:\s+[A-Z][a-z']+)?/g)) {
      out.add(match[0]);
    }
  }
  return [...out];
}



export default function BoothCall({
  result,
  cursor,
  complete = false,
  teams,
  feudHistory,
  raceNumber,
  season,
  league,
}: BoothCallProps) {
  /*
    D-EZ-74 — the join the booth is handed rather than performing itself.
    Rebuilt only when the garage actually changes, so the call below stays the
    pure memo it has always been.
  */
  const ctx = useMemo<BoothContext>(
    () => ({
      teams: teams ? new Map(teams.map((t) => [t.teamId, t.name])) : undefined,
      feudHistory,
      raceNumber,
      season,
      seasonNumber: season?.seasonNumber,
      league,
    }),
    [teams, feudHistory, raceNumber, season, league],
  );
  // Deterministic: the same race always produces the same call, so this is a
  // pure memo rather than something that drifts as the component re-renders.
  const call = useMemo(() => buildBroadcast(result, ctx), [result, ctx]);
  /** D-EZ-111 — the league's booth (the default three for an exhibition). */
  const cast: Cast = call.cast ?? DEFAULT_CAST;


  // ---- AI narration, strictly on top ------------------------------------
  //
  // D-027: the deterministic booth is the floor and it is already on screen by
  // the time any of this runs. `written` starts empty, so the FIRST render is
  // always the deterministic call — the model can only ever replace a line that
  // was already good enough to show. If the key is missing, the network is
  // down, or a line fails `verifyLine`, nothing here changes and nobody sees a
  // difference. There is no loading state because there is nothing to wait for.
  // ON BY DEFAULT WHEN IT IS THERE. It used to default off, which meant the
  // richest thing in the build was behind a checkbox most sittings never
  // touched. The safety argument for off-by-default was never about the model
  // being risky — every line still passes the same gate — it was about the
  // feature being unproven. It is proven. If `/api/narrate` does not answer the
  // toggle is not rendered at all, so this can only turn on something that
  // works.
  const [aiWanted, setAiWanted] = useState(true);
  const [aiOffered, setAiOffered] = useState(false);
  /** Sparse — index i holds the rewritten beat i once it lands. See below. */
  const [written, setWritten] = useState<readonly NarratedBeat[]>([]);

  useEffect(() => { void narrationAvailable().then(setAiOffered); }, []);
  const writtenRef = useRef(written); writtenRef.current = written;
  useEffect(() => { writtenRef.current = []; setWritten([]); }, [result]);
  const currentBeatIndex = call.beats.reduce((found, beat, i) => complete || beatEndsAt(beat) <= cursor ? i : found, -1);

  // Write only the call currently being watched. A distant finish must never
  // hold up today's action, and moving on cancels an obsolete request.
  //
  // The opening is already "published" the instant the race screen appears.
  // Rewriting it after the request returns makes the booth visibly contradict
  // itself: one introduction flashes, then another replaces it. Keep that
  // first impression deterministic and immutable. Later, turn-bound beats can
  // still receive the optional polish when they air.
  useEffect(() => {
    if (!aiWanted || !aiOffered) return;
    const index = currentBeatIndex;
    if (index < 0 || writtenRef.current[index]) return;
    const beat = call.beats[index];
    if (beat.kind === 'open') return;
    const stop = new AbortController();
    const previous = castCall(call.beats.slice(Math.max(0,index-2),index))
      .flatMap(b => [{who:b.speaker,line:b.line},...b.exchange]);
    const setting = buildSetting({result,turn:beatEndsAt(beat),lapsPerTurn:call.lapsPerTurn});
    // D-EZ-111 — the league's cast (as pool ids) and today's story bible ride along.
    void narrateBeat(beat,namesIn(beat),previous,null,setting,stop.signal,0,{cast,bible:call.bible}).then(rewritten => {
      if(stop.signal.aborted || !rewritten.aiWritten)return;
      setWritten(current => {const next=current.slice();next[index]=rewritten;return next;});
    }).catch(() => {});
    return () => stop.abort();
  }, [aiWanted,aiOffered,call,result,currentBeatIndex,cast]);

  // THE CAST IS NOT AN AI FEATURE. `castCall` assigns a speaker, a seat and the
  // rationed handoffs with no network in it at all, so who is talking — and the
  // fact that they talk to each other — is identical with the model off. What
  // the model changes is the polish of the words, not the shape of the
  // broadcast. `narrateCall` returns the same structure with better prose in it.
  const spoken = useMemo(() => castCall(call.beats), [call]);
  /*
    ⚠ BEAT BY BEAT, NOT ALL OR NOTHING. `written[i]` is the rewritten beat when
    it has arrived and a hole when it has not, and a hole falls through to the
    deterministic one — so the booth fills in as it goes instead of switching
    over in one jump at the end.
  */
  const beats: readonly NarratedBeat[] = useMemo(
    () => spoken.map((floor, i) => aiWanted ? written[i] ?? floor : floor),
    [spoken, written, aiWanted],
  );
  const aired = complete ? beats : beats.filter((b) => beatEndsAt(b) <= cursor);
  const onAir = aired[aired.length - 1] as NarratedBeat | undefined;
  const tape = useRef<HTMLDivElement | null>(null);
  const stick = useRef(true);
  const [following, setFollowing] = useState(true);
  /** When the booth itself scrolled the tape — not the reader. */
  const autoScrolledAt = useRef(0);
  /** D-EZ-111 — the latest call is read from its top when it is taller than the tape (the open, the wrap). */
  const showLatest = (el: HTMLDivElement) => {
    autoScrolledAt.current = Date.now();
    el.scrollTop = el.scrollHeight;
    const latest = el.querySelector<HTMLElement>('.booth__beat--current');
    if (!latest) return;
    const above = latest.getBoundingClientRect().top - el.getBoundingClientRect().top;
    if (above < 0) el.scrollTop += above - 4;
  };
  const onTapeScroll = useCallback(() => {
    const el = tape.current;
    if (!el) return;
    // Our own scroll to the latest call is still "following".
    if (Date.now() - autoScrolledAt.current < 250) return;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
    setFollowing(stick.current);
  }, []);
  // Advancing the race must not drag a reader away from an older update.
  useEffect(() => { stick.current = true; setFollowing(true); }, [result]);
  useLayoutEffect(() => {
    const el = tape.current;
    if (!el || !stick.current) return;
    showLatest(el);
  }, [aired.length, onAir?.line, onAir?.exchange.length]);
  const backToLive = () => {
    stick.current = true;
    setFollowing(true);
    if (tape.current) showLatest(tape.current);
  };
  const status = raceStatus(result, cursor);
  /*
    D-EZ-101 — ON A PHONE THE RADIO IS ONE LINE. The board comes first on a
    narrow screen and the radio under it shows only the latest call, with this
    to open the rest. On a wide screen the control is not drawn (CSS) and the
    whole tape shows as before.
  */
  const [allCalls, setAllCalls] = useState(false);
  if (!onAir) return null;

  return (
    <section className={`booth${allCalls ? ' is-all' : ''}`} aria-label="Race radio">
      <header className="booth__head">
        <span className="booth__badge">Race radio</span>
        <span className="booth__cond">{call.raceName}</span>
      </header>
      <dl className="booth__status" aria-label="Current race status">
        <div><dt>Flag</dt><dd>{status.flag}</dd></div>
        <div><dt>{status.flag === 'Finished' ? 'Winner' : 'Leader'}</dt><dd>{status.leader}</dd></div>
        <div><dt>Distance</dt><dd>{status.distance}</dd></div>
      </dl>
      <div className="booth__tape" ref={tape} onScroll={onTapeScroll} tabIndex={0} aria-label="Race commentary history">
        <ol className="booth__earlier">
          {aired.map((beat, index) => <Beat key={`${beat.turn}-${index}`} beat={beat} current={index === aired.length - 1} cast={cast} />)}
        </ol>
      </div>
      <footer className="booth__footer">
        {aired.length > 1 && (
          <button type="button" className="booth__more" aria-expanded={allCalls} onClick={() => setAllCalls((v) => !v)}>
            {allCalls ? 'Latest call only' : `Earlier calls (${aired.length - 1})`}
          </button>
        )}
        {following ? <span className="booth__following">{complete ? 'Race complete' : 'Following latest update'}</span> :
          <button type="button" className="booth__resume" onClick={backToLive}>Back to live</button>}
        <details className="booth__settings">
          <summary>Radio settings</summary>
          {aiOffered && <label className="booth__ai"><input type="checkbox" checked={aiWanted} onChange={e => setAiWanted(e.target.checked)} />Broadcast polish</label>}
          <p>{aiOffered && aiWanted ? 'Broadcast polish connected. Checked race facts remain the source.' : 'Commentary follows the recorded race facts.'}</p>
          <ul className="booth__castlist">{[cast.host, cast.analyst, cast.pit].map(m => <li key={m.id}><strong>{m.name}</strong> · {m.seat}<span className="booth__castbio">{m.bio}</span></li>)}</ul>
        </details>
      </footer>
    </section>
  );
}

/**
 * Who is talking. A badge rather than a name in the text, because "Ned Coyle:"
 * printed in front of every line turns a broadcast into a screenplay — and
 * because the eye finds a colour before it finds a word, which is the whole
 * reason a real broadcast cuts to a different camera.
 */
function Who({ id, big = false, cast = DEFAULT_CAST, seat = false }: { id: MemberId; big?: boolean; cast?: Cast; seat?: boolean }) {
  const m = cast[id];
  return (
    <span className={`booth__who booth__who--${id}${big ? ' booth__who--big' : ''}`} title={`${m.name} — ${m.seat}`}>
      <span className="booth__whoInitials" aria-hidden>{m.initials}</span>
      <span className="booth__whoName">{big || seat ? m.name : m.short}</span>
      {/* D-EZ-111 — the open introduces the booth: name and seat. */}
      {seat && <span className="booth__whoSeat"> · {m.seat}</span>}
    </span>
  );
}

const categoryLabel = {
  start: 'Pre-race', lead: 'Lead change', incident: 'Incident', pit: 'Pit road',
  restart: 'Restart', battle: 'Position battle', running: 'Race update', finish: 'Checkered flag',
};

/** The same readable structure for every moment, including the newest. */
export function Beat({ beat, current = false, cast = DEFAULT_CAST }: { beat: NarratedBeat; current?: boolean; cast?: Cast }) {
  const category = beat.broadcast?.category;
  // D-EZ-111 — register: 1 calm, 2 lively, 3 the booth on its feet.
  const energy = beat.broadcast?.energy ?? 2;
  const open = beat.kind === 'open';
  return <li className={`booth__beat${energy === 3 ? ' booth__beat--peak' : energy === 1 ? ' booth__beat--calm' : ''}${current ? ' booth__beat--current' : ''}`} aria-current={current ? 'true' : undefined}>
    <div className="booth__chapter"><span>{beat.turn ? `Turn ${beat.turn}` : 'Start'} · {category ? categoryLabel[category] : 'Race update'}{energy === 3 && category !== 'start' && <span className="booth__peak"> · Big moment</span>}</span>{current && <span>Latest</span>}</div>
    <div className="booth__speech">
      <Who id={beat.speaker} cast={cast} seat={open} />
      <p className="booth__prose">{beat.line}</p>
    </div>
    <Exchange beat={beat} showRefusals={false} cast={cast} seats={open} />
    <details className="booth__receipt"><summary>Race record</summary><BeatMeta beat={beat} />
      {beat.exchangeRefused > 0 && <Cite as="p">{refusalWords(beat)}</Cite>}
    </details>
  </li>;
}

/**
 * WHAT CAME BACK AT HIM — D-EZ-61.
 *
 * Indented under the line it answers, because that is what it is: an answer,
 * not a second report on the same moment. Nothing is rendered at all when the
 * exchange is empty, which is every routine beat and every beat with the model
 * switched off — a booth that shows an empty discussion panel on a car holding
 * station has told you nothing twice.
 */
/**
 * WHY A CONVERSATION STOPPED, in one clause a person can act on.
 *
 * ⚠ THE PHRASING SPLITS ON WHOSE FAULT IT IS, deliberately. Half of these mean
 * the gate did exactly what it is for and the reader should be reassured; the
 * other half mean a brief or a ceiling wants looking at, and the reader should
 * tell us. A single "refused" said both and therefore said neither.
 */
const REFUSAL_WORDS: Record<string, string> = {
  // The model reached for something nobody gave it. The gate is the feature.
  'invented-name': 'it named somebody who is not in this race',
  'invented-number': 'it used a number that is not in the facts',
  'unsupported-superlative': 'it called something the best or the worst, which the ledger never said',
  // The model wrote badly rather than untruthfully. Our side to fix.
  'too-long': 'the answer ran past the length a broadcast line gets',
  'too-many-sentences': 'the answer ran to too many sentences',
  'repeats-itself': 'it said the same thing twice in one breath',
  'echoes-a-recent-line': 'it repeated a line already said today',
  'contains-markup': 'it came back formatted rather than spoken',
  'empty': 'it came back empty',
};

const refusalWords = (beat: NarratedBeat): string => {
  const why = REFUSAL_WORDS[beat.exchangeRefusedBecause ?? ''] ?? 'the gate could not check it';
  return `The next one was cut because ${why}.`;
};

/** The receipt, on hover: the reason plus exactly what tripped it. */
const refusalTitle = (beat: NarratedBeat): string =>
  beat.exchangeRefusedBecause
    ? `${beat.exchangeRefusedBecause}${beat.exchangeRefusalDetail ? ` — ${beat.exchangeRefusalDetail}` : ''}`
    : 'The gate refused it — see the fact list';

export function Exchange({ beat, big = false, showRefusals: wantRefusals = true, cast = DEFAULT_CAST, seats = false }: { beat: NarratedBeat; big?: boolean; showRefusals?: boolean; cast?: Cast; seats?: boolean }) {
  // D-EZ-101 — why a polished exchange was cut is the editor's business; it
  // shows with "Show rulebook citations", not in the broadcast itself.
  const showRefusals = wantRefusals && useShowCitations();
  if (beat.exchange.length === 0 && (!showRefusals || beat.exchangeRefused === 0)) return null;
  return (
    <ol className={`booth__xch${big ? ' booth__xch--big' : ''}`}>
      {beat.exchange.map((turn, i) => (
        <li key={i} className="booth__xchturn">
          <Who id={turn.who} cast={cast} seat={seats && !beat.exchange.slice(0, i).some((x) => x.who === turn.who) && turn.who !== beat.speaker} />
          <span>{turn.line}</span>
        </li>
      ))}
      {showRefusals && beat.exchangeRefused > 0 && (
        /*
          SAID, NOT SWALLOWED. A turn the gate threw out ends the exchange there
          (see `narrateBeat`), and a conversation that stops early without
          saying why looks like a rendering fault rather than a refusal.
        */
        <li className="booth__xchcut" title={refusalTitle(beat)}>
          {beat.exchangeRefused} more {beat.exchangeRefused === 1 ? 'answer was' : 'answers were'}{' '}
          refused, so the exchange stops here.
          {/*
            D-EZ-78 — AND WHY. Howard, 21 Aug 2026: *"what does that mean that
            its stopping the exchanges? If there are natural exchanges set to
            happen why would we stop it?"* The count alone cannot tell him the
            difference between the gate catching an invented racetrack and a
            brief that wants shortening, and those want opposite responses.
          */}
          {beat.exchangeRefusedBecause && (
            <span className="booth__xchwhy"> {refusalWords(beat)}</span>
          )}
        </li>
      )}
    </ol>
  );
}

/**
 * The receipt. A beat is only worth anything if the claim under it can be
 * checked, so the citation and every fact are one click away.
 */
function BeatMeta({ beat }: { beat: BoothBeat }) {
  const has = beat.ruleCitation || beat.facts.length > 0 || beat.fellBack;
  if (!has) return null;
  return (
    <div className="booth__meta">
      {beat.ruleCitation && <Cite as="cite" className="booth__cite">{beat.ruleCitation}</Cite>}
      {beat.facts.length > 0 && (
        <ul className="booth__facts">
          {beat.facts.map((f, i) => (
            <li key={i}>
              <span className="booth__factkind">{f.kind}</span>
              {f.text}
            </li>
          ))}
        </ul>
      )}
      {beat.fellBack && (
        <Cite className="booth__fell">the engine's own words</Cite>
      )}
      {beat.turnsCovered > 1 && <span className="booth__covers">covers {beat.turnsCovered} turns</span>}
    </div>
  );
}
